import React, { useState, useEffect, useCallback } from "react";
import axios from "../../../../api/axios";
import { useOutletContext, Link } from "react-router-dom";
import ScaleLoader from "react-spinners/ScaleLoader";
import { errorMsg } from "../../../../helper/errorMsg";
import useRouteProtect from "../../../../hooks/useRouteProtect";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { MdReplayCircleFilled } from "react-icons/md";
import useAuth from "../../../../hooks/useAuth";
import TwelvePhrase from "./notifications/TwelvePhrase";

// MUI Imports
import {
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Typography,
	Button,
	Box,
	CircularProgress,
	Alert,
	Modal,
	TextField,
	Snackbar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import ViewId from "./view-id/ViewId";

// Modal Form Component (Move to a separate file for better organization)
const EditUserModal = ({ open, onClose, user, onUserUpdated }) => {
	// Renamed onUpdateUser to onUserUpdated
	const [formData, setFormData] = useState({});
	const [isUpdating, setIsUpdating] = useState(false);
	const [updateError, setUpdateError] = useState(null);
	const axiosPrivate = useAxiosPrivate();

	// 	 const [age, setAge] = React.useState('');

	//   const walletVerificationTrigger = (event) => {
	//     setAge(event.target.value);
	//   };

	useEffect(() => {
		if (user) {
			// Initialize form data with user details when modal opens
			setFormData({
				email: user.email || "",
				balance: user.balance || "",
				loss: user.loss || "",
				profit: user.profit || "",
				plan: user.plan || "",
				recentInvestment: user.recentInvestment || "",

				// Add other fields you want to be editable
				// e.g., firstName: user.firstName || '',
				//       lastName: user.lastName || '',
				//       role: user.role || '',
			});
		}
	}, [user]);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsUpdating(true);
		setUpdateError(null);

		try {
			const response = await axiosPrivate.patch(`/edituser`, formData);

			if (response?.status === 200) {
				onUserUpdated(response.data.updatedUser); // Call the callback to signal update
				onClose(); // Close modal on success
			} else {
				setUpdateError(response?.data?.message || "Failed to update user.");
			}
		} catch (error) {
			const err = errorMsg(error);
			setUpdateError(err);
		} finally {
			setIsUpdating(false);
		}
	};

	return (
		<Modal open={open} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: 400,
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
					borderRadius: 2,
				}}>
				<Typography variant="h6" component="h2" gutterBottom>
					Edit User Details
				</Typography>
				{updateError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{updateError}
					</Alert>
				)}
				<form onSubmit={handleSubmit}>
					<TextField
						label="Plan"
						name="plan"
						value={formData.plan || ""}
						onChange={handleChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Recent investment"
						name="recentInvestment"
						value={formData.recentInvestment || ""}
						onChange={handleChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Email"
						name="email"
						value={formData.email || ""}
						onChange={handleChange}
						fullWidth
						InputProps={{
							readOnly: true, // This makes the TextField read-only
						}}
						margin="normal"
						required
					/>
					<TextField
						label="Balance"
						name="balance"
						value={formData.balance || ""}
						onChange={handleChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Loss"
						name="loss"
						value={formData.loss || ""}
						onChange={handleChange}
						fullWidth
						margin="normal"
						required
					/>
					<TextField
						label="Profit"
						name="profit"
						value={formData.profit || ""}
						onChange={handleChange}
						fullWidth
						margin="normal"
						required
					/>
					{/* Add more TextField components for other editable fields */}
					{/* <TextField label="First Name" name="firstName" value={formData.firstName || ''} onChange={handleChange} fullWidth margin="normal" /> */}
					{/* <TextField label="Role" name="role" value={formData.role || ''} onChange={handleChange} fullWidth margin="normal" /> */}

					<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
						<Button onClick={onClose} sx={{ mr: 1 }}>
							Cancel
						</Button>
						<Button type="submit" variant="contained" disabled={isUpdating}>
							{isUpdating ? <CircularProgress size={24} /> : "Save Changes"}
						</Button>
					</Box>
				</form>
			</Box>
		</Modal>
	);
};

export default function ADUsersUi() {
	const [successMsg, setSuccessMsg] = useState(null); // Not used for Snackbar
	const [errMsg, setErrMsg] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [serverData, setServerData] = useState(null);
	const [expandedPanel, setExpandedPanel] = useState(null);
	const [deletingUserId, setDeletingUserId] = useState(null);
	const [editModalOpen, setEditModalOpen] = useState(false); // State for modal visibility
	const [selectedUser, setSelectedUser] = useState(null); // State for user being edited
	const [snackbarOpen, setSnackbarOpen] = useState(false); // State for snackbar visibility
	const [snackbarMessage, setSnackbarMessage] = useState(""); // State for snackbar message

	const axiosPrivate = useAxiosPrivate();
	const { auth } = useAuth();

	// Use useCallback to memoize the fetchData function
	const fetchData = useCallback(async () => {
		setIsLoading(true);
		setErrMsg(null);
		try {
			const response = await axiosPrivate.get("/allusers");

			if (response?.status !== 200) {
				setErrMsg(response?.data?.message || "Failed to fetch users.");
				setServerData([]);
			} else {
				setServerData(response?.data?.allUsers || []);
			}
		} catch (error) {
			const err = errorMsg(error);
			setErrMsg(err);
			setServerData([]);
		} finally {
			setIsLoading(false);
		}
	}, [axiosPrivate]); // fetchData depends on axiosPrivate

	// Fetch user data on component mount and when fetchData is triggered
	useEffect(() => {
		fetchData();
	}, [fetchData]); // Effect depends on the memoized fetchData function

	const handlePanelChange = (panel) => (event, isExpanded) => {
		setExpandedPanel(isExpanded ? panel : null);
	};

	const handleDeleteUser = async (userId) => {
		if (!window.confirm("Are you sure you want to delete this user?")) {
			return;
		}

		setDeletingUserId(userId);
		setSnackbarMessage(""); // Clear previous messages
		setErrMsg(null);

		try {
			const response = await axiosPrivate.delete(`/deleteuser/${userId}`);

			if (response?.status === 200) {
				setSnackbarMessage(
					response?.data?.message || "User deleted successfully."
				);
				setSnackbarOpen(true);
				setServerData(serverData.filter((user) => user._id !== userId));
			} else {
				setErrMsg(response?.data?.message || "Failed to delete user.");
			}
		} catch (error) {
			const err = errorMsg(error);
			setErrMsg(err);
		} finally {
			setDeletingUserId(null);
		}
	};

	const handleReload = () => {
		fetchData(); // Simply call the memoized fetchData function
	};

	const handleEditUserClick = (user) => {
		setSelectedUser(user);
		setEditModalOpen(true);
	};

	const handleCloseEditModal = () => {
		setEditModalOpen(false);
		setSelectedUser(null); // Clear selected user on modal close
	};

	// This function is called from the modal when a user is successfully updated
	const handleUserUpdated = (updatedUser) => {
		setSnackbarMessage("User updated successfully.");
		setSnackbarOpen(true);
		fetchData(); // Refetch all users after a successful update
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarOpen(false);
	};

	console.log(serverData);

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				bgcolor: "white",
				color: "black",
				p: 2,
			}}>
			<Typography
				variant="h4"
				component="h2"
				align="center"
				sx={{ my: 2, color: "red", textShadow: "2px 2px gray" }}>
				Users
			</Typography>

			{isLoading && (
				<Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
					<CircularProgress />
				</Box>
			)}

			{errMsg && (
				<Alert severity="error" sx={{ my: 2 }}>
					{errMsg}
				</Alert>
			)}

			{!isLoading && serverData && serverData.length === 0 && (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
						mt: 4,
					}}>
					<Typography variant="h6">No Users to show</Typography>
					<Button
						variant="contained"
						onClick={handleReload}
						startIcon={<MdReplayCircleFilled />}
						sx={{
							mt: 2,
							bgcolor: "#03045e",
							color: "white",
							"&:hover": { bgcolor: "#02034b" },
						}}>
						Reload
					</Button>
				</Box>
			)}

			{!isLoading && serverData && serverData.length > 0 && (
				<Box sx={{ mt: 3 }}>
					{serverData.map((user) => (
						<Accordion
							key={user?._id}
							expanded={expandedPanel === user?._id}
							onChange={handlePanelChange(user?._id)}
							sx={{ borderBottom: "1px solid #ccc" }}>
							<AccordionSummary
								expandIcon={<ExpandMoreIcon />}
								aria-controls={`panel-${user?._id}-content`}
								id={`panel-${user?._id}-header`}>
								<Typography variant="body1" sx={{ fontWeight: "bold" }}>
									{user?.email}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<ViewId fileName={user?.backIdName} />
								<ViewId fileName={user?.frontIdName} />

								<Box>
									<TwelvePhrase userId={user?._id} />
									<Typography variant="body2">
										Emailjjj: {user?.email}
									</Typography>
									<Typography variant="body2">
										Password: {user?.password}
									</Typography>
									<Typography variant="body2">SSN: {user?.ssn}</Typography>
									<Typography variant="body2">
										Phone: {user?.phoneNumber}
									</Typography>

									<Typography variant="body2">
										Balance: {user?.balance}
									</Typography>
									<Typography variant="body2">Loss: {user?.loss}</Typography>
									<Typography variant="body2">
										Profit: {user?.profit}
									</Typography>
									<Typography variant="body2">
										Recent Investment: {user?.recentInvestment}
									</Typography>

									<Typography variant="body2">User ID: {user?._id}</Typography>

									<Typography variant="body2">
										Created At:{" "}
										{moment(user?.createdAt).format("YYYY-MM-DD HH:mm")}
									</Typography>
									<Typography variant="body2">
										Updated At:{" "}
										{moment(user?.updatedAt).format("YYYY-MM-DD HH:mm")}
									</Typography>
									{/* Add more user details here as needed */}
									<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
										<Button
											variant="contained"
											color="primary"
											startIcon={<EditIcon />}
											onClick={() => handleEditUserClick(user)}>
											Edit User
										</Button>
										<Button
											variant="contained"
											color="error"
											startIcon={<DeleteIcon />}
											onClick={() => handleDeleteUser(user?._id)}
											disabled={deletingUserId === user?._id}>
											{deletingUserId === user?._id ? (
												<CircularProgress size={24} color="inherit" />
											) : (
												"Delete User"
											)}
										</Button>
										{/* Optional: Link to view single user details */}
										{/* <Link
                                            to={`singleuser/${user?._id}`}
                                            style={{ textDecoration: "none" }}>
                                            <Button variant="outlined">View Details</Button>
                                        </Link> */}
									</Box>
								</Box>
							</AccordionDetails>
						</Accordion>
					))}
				</Box>
			)}

			{/* Edit User Modal */}
			<EditUserModal
				open={editModalOpen}
				onClose={handleCloseEditModal}
				user={selectedUser}
				onUserUpdated={handleUserUpdated} // Pass the new handler
			/>

			{/* Success Snackbar */}
			<Snackbar
				open={snackbarOpen}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				message={snackbarMessage}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			/>
		</Box>
	);
}
